import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supportApi, TicketListParams, TicketUpdatePayload, SendReplyPayload, DecidePayload } from '../api/supportApi'
import { useToast } from '@/hooks/use-toast'
import type { TicketStatus } from '@/types/entities'

// ─── Query Keys ──────────────────────────────────────

const SUPPORT_STATS_KEY = 'support-stats'
const SUPPORT_TICKETS_KEY = 'support-tickets'
const SUPPORT_TICKET_KEY = 'support-ticket'
const SUPPORT_MESSAGES_KEY = 'support-messages'
const SUPPORT_ACTIONS_KEY = 'support-actions'
const SUPPORT_AGENT_TASKS_KEY = 'support-agent-tasks'
const SUPPORT_CONTACTS_KEY = 'support-contacts'
const SUPPORT_CANNED_KEY = 'support-canned-responses'
const SUPPORT_CONFIG_KEY = 'support-agent-config'
const SUPPORT_DECISION_KEY = 'support-decision'

// ─── Dashboard ───────────────────────────────────────

export function useSupportStats() {
  return useQuery({
    queryKey: [SUPPORT_STATS_KEY],
    queryFn: () => supportApi.getStats(),
    refetchInterval: 15000,
  })
}

// ─── Tickets ─────────────────────────────────────────

export function useTickets(params: TicketListParams = {}) {
  return useQuery({
    queryKey: [SUPPORT_TICKETS_KEY, params],
    queryFn: () => supportApi.getTickets(params),
    refetchInterval: 10000,
  })
}

export function useTicket(ticketId: string | null) {
  return useQuery({
    queryKey: [SUPPORT_TICKET_KEY, ticketId],
    queryFn: () => supportApi.getTicket(ticketId!),
    enabled: !!ticketId,
  })
}

export function useUpdateTicket() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ ticketId, data }: { ticketId: string; data: TicketUpdatePayload }) =>
      supportApi.updateTicket(ticketId, data),
    onSuccess: (ticket) => {
      queryClient.invalidateQueries({ queryKey: [SUPPORT_TICKETS_KEY] })
      queryClient.invalidateQueries({ queryKey: [SUPPORT_TICKET_KEY, ticket.id] })
      queryClient.invalidateQueries({ queryKey: [SUPPORT_STATS_KEY] })
      toast({ title: 'Ticket updated', description: `Ticket updated successfully.` })
    },
    onError: (error: Error) => {
      toast({ title: 'Update failed', description: error.message, variant: 'destructive' })
    },
  })
}

export function useUpdateTicketStatus() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ ticketId, status }: { ticketId: string; status: TicketStatus }) =>
      supportApi.updateTicketStatus(ticketId, status),
    onSuccess: (ticket) => {
      queryClient.invalidateQueries({ queryKey: [SUPPORT_TICKETS_KEY] })
      queryClient.invalidateQueries({ queryKey: [SUPPORT_TICKET_KEY, ticket.id] })
      queryClient.invalidateQueries({ queryKey: [SUPPORT_STATS_KEY] })
      toast({ title: 'Status updated', description: `Ticket marked as ${ticket.status}.` })
    },
    onError: (error: Error) => {
      toast({ title: 'Status update failed', description: error.message, variant: 'destructive' })
    },
  })
}

// ─── Messages ────────────────────────────────────────

export function useMessages(ticketId: string | null) {
  return useQuery({
    queryKey: [SUPPORT_MESSAGES_KEY, ticketId],
    queryFn: () => supportApi.getMessages(ticketId!),
    enabled: !!ticketId,
    refetchInterval: 5000,
  })
}

export function useSendReply() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ ticketId, payload }: { ticketId: string; payload: SendReplyPayload }) =>
      supportApi.sendReply(ticketId, payload),
    onSuccess: (_data, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: [SUPPORT_MESSAGES_KEY, ticketId] })
      queryClient.invalidateQueries({ queryKey: [SUPPORT_TICKET_KEY, ticketId] })
      queryClient.invalidateQueries({ queryKey: [SUPPORT_DECISION_KEY, ticketId] })
      queryClient.invalidateQueries({ queryKey: [SUPPORT_TICKETS_KEY] })
      queryClient.invalidateQueries({ queryKey: [SUPPORT_STATS_KEY] })
      toast({ title: 'Reply sent', description: 'Your reply has been sent to the customer.' })
    },
    onError: (error: Error) => {
      toast({ title: 'Reply failed', description: error.message, variant: 'destructive' })
    },
  })
}

// ─── Decisions ───────────────────────────────────────

export function useDecision(ticketId: string | null, enabled = true) {
  return useQuery({
    queryKey: [SUPPORT_DECISION_KEY, ticketId],
    queryFn: () => supportApi.getDecision(ticketId!),
    enabled: !!ticketId && enabled,
    // It reads Shopify and Prodigi; there is no need to ask again while the page is open.
    staleTime: 60000,
  })
}

export function useDecide() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ ticketId, payload }: { ticketId: string; payload: DecidePayload }) =>
      supportApi.decide(ticketId, payload),
    onSuccess: (_data, { ticketId, payload }) => {
      for (const key of [SUPPORT_DECISION_KEY, SUPPORT_MESSAGES_KEY, SUPPORT_TICKET_KEY, SUPPORT_ACTIONS_KEY]) {
        queryClient.invalidateQueries({ queryKey: [key, ticketId] })
      }
      toast({
        title: payload.outcome === 'approved' ? 'Approved and done' : 'Declined',
        description: 'A notification is drafted below. Read it, then send it.',
      })
    },
    onError: (error: Error & { response?: { data?: { error?: string } } }) => {
      toast({
        title: 'Not done',
        description: error.response?.data?.error || error.message,
        variant: 'destructive',
      })
    },
  })
}

// ─── Agent Actions ───────────────────────────────────

export function useAgentActions(ticketId: string | null) {
  return useQuery({
    queryKey: [SUPPORT_ACTIONS_KEY, ticketId],
    queryFn: () => supportApi.getAgentActions(ticketId!),
    enabled: !!ticketId,
  })
}

// ─── Contacts ────────────────────────────────────────

export function useContacts(params: { search?: string; limit?: number; offset?: number } = {}) {
  return useQuery({
    queryKey: [SUPPORT_CONTACTS_KEY, params],
    queryFn: () => supportApi.getContacts(params),
  })
}

// ─── Canned Responses ────────────────────────────────

export function useCannedResponses() {
  return useQuery({
    queryKey: [SUPPORT_CANNED_KEY],
    queryFn: () => supportApi.getCannedResponses(),
  })
}

export function useCreateCannedResponse() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: { title: string; body: string; category: string }) =>
      supportApi.createCannedResponse(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUPPORT_CANNED_KEY] })
      toast({ title: 'Response created', description: 'Canned response saved.' })
    },
    onError: (error: Error) => {
      toast({ title: 'Create failed', description: error.message, variant: 'destructive' })
    },
  })
}

export function useDeleteCannedResponse() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: string) => supportApi.deleteCannedResponse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUPPORT_CANNED_KEY] })
      toast({ title: 'Response deleted' })
    },
    onError: (error: Error) => {
      toast({ title: 'Delete failed', description: error.message, variant: 'destructive' })
    },
  })
}

// ─── Agent Config ────────────────────────────────────

export function useAgentConfig() {
  return useQuery({
    queryKey: [SUPPORT_CONFIG_KEY],
    queryFn: () => supportApi.getAgentConfig(),
  })
}

export function useUpdateAgentConfig() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      supportApi.updateAgentConfig(key, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUPPORT_CONFIG_KEY] })
      toast({ title: 'Config updated', description: 'Agent configuration saved.' })
    },
    onError: (error: Error) => {
      toast({ title: 'Config update failed', description: error.message, variant: 'destructive' })
    },
  })
}

// ─── Helpers ─────────────────────────────────────────

export function getTicketStatusLabel(status: TicketStatus): string {
  const labels: Record<TicketStatus, string> = {
    new: 'New',
    agent_processing: 'Agent Processing',
    awaiting_customer: 'Awaiting Customer',
    awaiting_operator: 'Needs Attention',
    operator_replied: 'Replied',
    resolved: 'Resolved',
    closed: 'Closed',
    spam: 'Spam',
  }
  return labels[status] || status
}

export function getTicketStatusVariant(
  status: TicketStatus
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'new':
      return 'default'
    case 'agent_processing':
      return 'secondary'
    case 'awaiting_operator':
      return 'destructive'
    case 'awaiting_customer':
    case 'operator_replied':
      return 'outline'
    case 'resolved':
    case 'closed':
      return 'secondary'
    case 'spam':
      return 'secondary'
    default:
      return 'default'
  }
}

export function getIntentLabel(intent: string | null): string {
  if (!intent) return 'Unknown'
  const labels: Record<string, string> = {
    order_status: 'Order Status',
    fulfillment_tracking: 'Tracking',
    artwork_inquiry: 'Artwork',
    return_exchange: 'Return/Exchange',
    artist_inquiry: 'Artist',
    general: 'General',
    spam: 'Spam',
  }
  return labels[intent] || intent
}

export function getSentimentLabel(sentiment: string | null): string {
  if (!sentiment) return ''
  return sentiment.charAt(0).toUpperCase() + sentiment.slice(1)
}

export function getPriorityLabel(priority: string): string {
  return priority.charAt(0).toUpperCase() + priority.slice(1)
}

export function usePromptAgent() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ ticketId, prompt, parentTaskId }: { ticketId: string; prompt: string; parentTaskId?: string | null }) =>
      supportApi.promptAgent(ticketId, prompt, parentTaskId),
    onSuccess: (result) => {
      const failed = result.status === 'needs_attention'
      toast({ title: failed ? 'Task needs attention' : result.status === 'waiting' ? 'Waiting for publishing' : 'Task completed',
        description: 'Review the results and audit trail below.',
        variant: failed ? 'destructive' : 'default' })
    },
    onError: (error: Error) => {
      toast({ title: 'Agent task failed', description: error.message, variant: 'destructive' })
    },
    onSettled: (_data, _error, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: [SUPPORT_ACTIONS_KEY, ticketId] })
      queryClient.invalidateQueries({ queryKey: [SUPPORT_AGENT_TASKS_KEY, ticketId] })
      queryClient.invalidateQueries({ queryKey: ['artworks'] })
    },
  })
}

export function useAgentTasks(ticketId: string) {
  return useQuery({
    queryKey: [SUPPORT_AGENT_TASKS_KEY, ticketId],
    queryFn: () => supportApi.getAgentTasks(ticketId),
    refetchInterval: (query) => query.state.data?.some(task => task.status !== 'completed') ? 5000 : false,
  })
}
